import React, { useState } from 'react';
import { InspectionField } from '../../services/inspectionsApi';
import { fmtDateTime } from '../../utils/format';

/**
 * Ritar formuläret för en egenkontroll utifrån mallens fält.
 * Används både inloggat (Inspections) och via länk utan inloggning (InspectionPage).
 */

export type Answers = Record<string, any>;
export type Prefill = Record<string, { value: number; timestamp: string; device_name: string }>;

interface Props {
    fields: InspectionField[];
    answers: Answers;
    prefill?: Prefill;
    readOnly?: boolean;
    onChange?: (key: string, value: any) => void;
    /** Fältnycklar som avvek (markeras med röd kant). */
    deviationKeys?: string[];
}

const label: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14, color: '#333' };
const help: React.CSSProperties = { fontSize: 12, color: '#777', marginTop: 2 };
const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6,
    padding: '9px 10px', fontSize: 15, fontFamily: 'inherit', background: 'white',
};

const fmtTime = (iso: string) => fmtDateTime(iso);

/**
 * Fyller tomma kontaktfält med namnet på den som gör kontrollen.
 * Ett fält som fortfarande har det gamla namnet byts också, så att det följer med
 * när man ändrar sitt namn på länksidan. Egna ändringar rörs inte.
 */
export const fillContactFields = (fields: InspectionField[], answers: Answers, name: string, previous = ''): Answers => {
    if (!name.trim()) return answers;
    let out = answers;
    for (const f of fields) {
        if (f.type !== 'text' || !f.contact) continue;
        const v = answers[f.key];
        if (v == null || v === '' || (previous && v === previous)) {
            if (out === answers) out = { ...answers };
            out[f.key] = name.trim();
        }
    }
    return out;
};

/** Färdigt svar i läsläge, som text. */
export const answerText = (f: InspectionField, v: any): string => {
    // Obesvarad kryssruta (t.ex. i förhandsvisning av en planerad kontroll) visas som streck, inte som "Nej".
    if (f.type === 'checkbox') return v == null ? '–' : v ? 'Ja' : 'Nej';
    if (v == null || v === '') return '–';
    if (f.type === 'number' && f.unit) return `${v} ${f.unit}`;
    if (f.type === 'date') { try { return new Date(String(v) + 'T00:00:00').toLocaleDateString('sv-SE'); } catch { return String(v); } }
    return String(v);
};

const hasValue = (v: any) => v !== undefined && v !== null && v !== '';

const InspectionForm: React.FC<Props> = ({ fields, answers, prefill = {}, readOnly = false, onChange, deviationKeys = [] }) => {
    // Valfria fält (utom kryssrutor) ligger hopfällda tills man klickar på dem, så att
    // formuläret inte tar onödig plats. Fält som redan har ett värde visas utfällda.
    const [opened, setOpened] = useState<Set<string>>(() => new Set());
    const open = (key: string) => setOpened(prev => { const n = new Set(prev); n.add(key); return n; });
    const set = (key: string, value: any) => { if (!readOnly && onChange) onChange(key, value); };
    const isDev = (key: string) => deviationKeys.includes(key);
    const collapsed = (f: InspectionField, v: any) =>
        !readOnly && !f.required && f.type !== 'checkbox' && !opened.has(f.key) && !hasValue(v) && !isDev(f.key);
    const box = (key: string): React.CSSProperties => ({
        padding: '12px 14px', borderRadius: 8, marginBottom: 10,
        border: isDev(key) ? '2px solid #dc3545' : '1px solid #e5e5e5',
        background: isDev(key) ? '#fff5f5' : '#fafafa',
    });

    return (
        <div>
            {fields.map(f => {
                if (f.type === 'heading') {
                    return (
                        <h3 key={f.key} style={{ margin: '22px 0 10px', fontSize: 16, color: '#0f3460', borderBottom: '1px solid #dee2e6', paddingBottom: 4 }}>
                            {f.label}
                        </h3>
                    );
                }
                const v = answers[f.key];
                const req = f.required ? <span style={{ color: '#dc3545' }}> *</span> : null;

                if (collapsed(f, v)) {
                    return (
                        <button key={f.key} type="button" onClick={() => open(f.key)} style={{
                            ...box(f.key), width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                            padding: '9px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                        }}>
                            <span style={{ ...label, marginBottom: 0, fontWeight: 500, color: '#555' }}>{f.label} <span style={{ fontWeight: 400, color: '#999', fontSize: 12 }}>(valfritt)</span></span>
                            <span style={{ color: '#1a4aa6', fontSize: 13, whiteSpace: 'nowrap' }}>Fyll i ▾</span>
                        </button>
                    );
                }

                if (readOnly) {
                    return (
                        <div key={f.key} style={box(f.key)}>
                            <div style={{ ...label, marginBottom: 2 }}>{f.label}</div>
                            <div style={{ fontSize: 15 }}>{answerText(f, v)}</div>
                            {f.help && <div style={help}>{f.help}</div>}
                        </div>
                    );
                }

                switch (f.type) {
                    case 'checkbox':
                        return (
                            <div key={f.key} style={box(f.key)}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 15 }}>
                                    <input type="checkbox" checked={!!v} onChange={e => set(f.key, e.target.checked)} style={{ width: 20, height: 20 }} />
                                    <span>{f.label}{req}</span>
                                </label>
                                {f.help && <div style={{ ...help, marginLeft: 30 }}>{f.help}</div>}
                            </div>
                        );
                    case 'select':
                        return (
                            <div key={f.key} style={box(f.key)}>
                                <span style={label}>{f.label}{req}</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {(f.options ?? []).map(o => {
                                        const on = v === o;
                                        const bad = (f.deviationValues ?? []).includes(o);
                                        return (
                                            <button key={o} type="button" onClick={() => set(f.key, on ? null : o)} style={{
                                                padding: '8px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit',
                                                border: `1px solid ${on ? (bad ? '#dc3545' : '#0f3460') : '#ccc'}`,
                                                background: on ? (bad ? '#dc3545' : '#0f3460') : 'white',
                                                color: on ? 'white' : '#333',
                                            }}>{o}</button>
                                        );
                                    })}
                                </div>
                                {f.help && <div style={help}>{f.help}</div>}
                            </div>
                        );
                    case 'date':
                        return (
                            <div key={f.key} style={box(f.key)}>
                                <label style={label}>{f.label}{req}</label>
                                <input type="date" value={v ?? ''} onChange={e => set(f.key, e.target.value || null)} style={{ ...input, maxWidth: 220 }} />
                                {f.maxAgeDays != null && <div style={help}>Får vara högst {f.maxAgeDays} dagar gammalt.</div>}
                                {f.help && <div style={help}>{f.help}</div>}
                            </div>
                        );
                    case 'number': {
                        const p = prefill[f.key];
                        const limits = [f.min != null ? `lägst ${f.min}` : null, f.max != null ? `högst ${f.max}` : null].filter(Boolean).join(', ');
                        return (
                            <div key={f.key} style={box(f.key)}>
                                <label style={label}>{f.label}{req}</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <input type="number" step="any" inputMode="decimal" value={v ?? ''} onChange={e => set(f.key, e.target.value === '' ? null : e.target.value)} style={{ ...input, maxWidth: 160 }} />
                                    {f.unit && <span style={{ color: '#555' }}>{f.unit}</span>}
                                    {p && (
                                        <button type="button" onClick={() => set(f.key, p.value)} style={{ background: '#e8f0fe', border: '1px solid #b6ccf5', color: '#1a4aa6', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                                            Givare {p.device_name}: {p.value}{f.unit ? ` ${f.unit}` : ''} ({fmtTime(p.timestamp)})
                                        </button>
                                    )}
                                </div>
                                {(limits || f.help) && <div style={help}>{[limits ? `Ska vara ${limits}${f.unit ? ' ' + f.unit : ''}.` : '', f.help ?? ''].filter(Boolean).join(' ')}</div>}
                            </div>
                        );
                    }
                    case 'text':
                    default:
                        return (
                            <div key={f.key} style={box(f.key)}>
                                <label style={label}>{f.label}{req}</label>
                                {f.multiline
                                    ? <textarea value={v ?? ''} onChange={e => set(f.key, e.target.value)} rows={3} style={{ ...input, resize: 'vertical' }} />
                                    : <input type="text" value={v ?? ''} onChange={e => set(f.key, e.target.value)} style={input} />}
                                {f.help && <div style={help}>{f.help}</div>}
                            </div>
                        );
                }
            })}
        </div>
    );
};

export default InspectionForm;
